import React, { useState, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Dashboard from "../../pages/Dashboard";
import TopBar from "./TopBar";
import Navbar from "../Navbar";
import ItemReturn from "../../pages/POS/ItemReturn";
import Damages from "../../pages/POS/Damages";
import CreditRepayments from "../../pages/Transactions/CreditRepayments";
import ExpenseTransaction from "../../pages/Transactions/ExpenseTransaction";
import AuthorizeRequisition from "../../pages/Authorization/AuthorizeRequisition";
import BranchRequisitionDetails from "../../pages/Authorization/BranchRequisitionDetails";
import AuthorizeInterbranch from "../../pages/Authorization/AuthorizeInterbranch";
import InterbranchDetail from "../../pages/Authorization/InterbranchDetail";
import ReportRoutes from "../../routes/ReportRoutes";
import Cashier from "../../pages/POS/Cashier";
import StockRequisition from "../../pages/POS/StockRequisition";
import NewRequisition from "../../pages/POS/NewRequisition";
import StockRequisitionDetail from "../../pages/POS/StockRequisitionDetail";
import CreditSales from "../../pages/POS/CreditSales";
import SupplierTransactionList from "../../pages/Transactions/SupplierTransactionList";
import SupplierStatement from "../../pages/Transactions/SupplierStatement";
import BranchOutstandingStock from "../../pages/POS/BranchOutstandingStock";
import BranchOUtstandingStockDetails from "../../pages/POS/BranchOutstandingStockDetails";
import CashierItemReturn from "../../pages/POS/CashierItemReturn";
import ChangePassword from "../../pages/ChangePassword";
import SalesRoutes from "../../routes/SalesRoutes";
import CreditRepaymentDetails from "../../pages/Transactions/CreditRepaymentDetails";
import ItemReturnList from "../../pages/POS/ItemReturnList";
import ItemReturnDetails from "../../pages/POS/ItemReturnDetails";
import PrintExpenseTransaction from "../../pages/Transactions/PrintExpenseTransaction";
import AdministrationRoutes from "../../routes/AdministrationRoutes";
import InventoryRoutes from "../../routes/InventoryRoutes";
import BatchTracking from "../../pages/Cashier/Inventory/BatchTracking";
import NotFound404 from "../../pages/error/NotFound404";
import RoleBasedRoutes from "../../routes/RoleBasedRoutes";
import DispenserDashboard from "../../pages/dashboard/DispenserDashboard";
import AdminDashboard from "../../pages/dashboard/AdminDashboard";
import DispatchShipment from "../../pages/Authorization/DispatchShipment";
import AdminItemReturnList from "../../pages/Inventory/sale/AdminItemReturnList";
import { useGlobalContext } from "../../context/stateContext";
import { toast } from "react-toastify";
import InventoryUpdateModal from "../modals/InventoryUpdateModal";
import OfflineModal from "../modals/OfflineModal";
import SuperAdminRoutes from "../../routes/roles/SuperAdminRoutes";
import DispenserRoutes from "../../routes/roles/DispenserRoutes";
import AdminRoutes from "../../routes/roles/AdminRoutes";
import Unauthorized from "../../pages/error/Unauthorized";
import InvoiceRoutes from "../../routes/InvoiceRoutes";
import TransactionRoutes from "../../routes/TransactionRoutes";
import AuditorRoutes from "../../routes/roles/AuditorRoutes";
import WarehouseManagerRoutes from "../../routes/roles/WarehouseManagerRoutes";
import BranchManagerRoutes from "../../routes/roles/BranchManagerRoutes";
import StoreManagerRoutes from "../../routes/roles/StoreManagerRoutes";
import DrugRequisitionReport from "../../pages/Authorization/DrugRequisitionReport";
import BranchItemReturnList from "../../pages/Inventory/sale/BranchItemReturnList";

const Layout = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const { priceUpdateAvailable } = useGlobalContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    if (priceUpdateAvailable) {
      setIsModalOpen(true);
    }
  }, [priceUpdateAvailable]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineModal(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineModal(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check connection status on mount
    if (!navigator.onLine) {
      setShowOfflineModal(true);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const isPriceUpdatePage = location.pathname === "/inventory/price-updates/";

  return (
    <div className="min-h-screen">
      {isModalOpen && !isPriceUpdatePage && <InventoryUpdateModal />}

      <TopBar />
      <Navbar />

      {/* Offline Warning Modal */}
      {showOfflineModal && (
        <OfflineModal
          setShowOfflineModal={setShowOfflineModal}
          navigator={navigator}
        />
      )}

      {/* Online Indicator - Small notification when back online */}
      {isOnline && !showOfflineModal && (
        <div className="fixed top-24 right-4 z-[90]">
          <div className="bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-slideIn">
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-sm font-medium">Online</span>
          </div>
        </div>
      )}

      {/* Main content with padding to account for fixed headers */}
      <div className="pt-[120px]">
        <Routes>
          {/* SUPER ADMIN */}
          <Route
            path="/super-admin/*"
            element={
              <RoleBasedRoutes
                allowedRoles={["superadmin"]}
                component={SuperAdminRoutes}
                fallback="/not-found"
              />
            }
          />

          {/* DISPENSER */}
          <Route
            path="/dispenser/*"
            element={
              <RoleBasedRoutes
                allowedRoles={["dispenser", "branch_manager"]}
                component={DispenserRoutes}
                fallback="/not-found"
              />
            }
          />

          {/* ADMIN */}
          <Route
            path="/admin/*"
            element={
              <RoleBasedRoutes
                allowedRoles={["admin"]}
                component={AdminRoutes}
                fallback="/not-found"
              />
            }
          />

          {/* IT AUDITOR */}
          <Route
            path="/auditor/*"
            element={
              <RoleBasedRoutes
                allowedRoles={["it_auditor"]}
                component={AuditorRoutes}
                fallback="/not-found"
              />
            }
          />

          {/* WAREHOUSE MANAGER */}
          <Route
            path="/warehouse-manager/*"
            element={
              <RoleBasedRoutes
                allowedRoles={["warehouse_manager"]}
                component={WarehouseManagerRoutes}
                fallback="/not-found"
              />
            }
          />

          {/* BRANCH MANAGER */}
          <Route
            path="/branch-manager/*"
            element={
              <RoleBasedRoutes
                allowedRoles={["branch_manager"]}
                component={BranchManagerRoutes}
                fallback="/not-found"
              />
            }
          />

          {/* STORE MANAGER */}
          <Route
            path="/store-manager/*"
            element={
              <RoleBasedRoutes
                allowedRoles={["stores"]}
                component={StoreManagerRoutes}
                fallback="/not-found"
              />
            }
          />
          
          <Route path="/pos/return" element={
            <RoleBasedRoutes
                allowedRoles={["admin", "superadmin"]}
                component={AdminItemReturnList}
                fallback="/not-found"
              />
          } />
          <Route path="/pos/return/branch" element={<BranchItemReturnList />} />
          <Route path="/pos/return/drugs" element={<ItemReturnList />} />
          <Route
            path="/pos/return/part/:sale_id"
            element={<ItemReturnDetails />}
          />

          {/* INVENTORY */}
          <Route path="/inventory/*" element={<InventoryRoutes />} />

          <Route
            path="/transaction/repayment/credit"
            element={<CreditRepayments />}
          />
          <Route
            path="/transaction/repayment/credit/:credit_id"
            element={<CreditRepaymentDetails />}
          />
          <Route
            path="/transaction/supplier"
            element={<SupplierTransactionList />}
          />
          <Route
            path="/transaction/supplier/statement/:supplier_id"
            element={<SupplierStatement />}
          />
          <Route
            path="/transaction/*"
            element={
              <RoleBasedRoutes
                allowedRoles={["superadmin", "admin", "warehouse_manager"]}
                component={TransactionRoutes}
                fallback="/not-found"
              />
            }
          />

          {/* AUTHORIZATION */}
          <Route
            element={
              <RoleBasedRoutes
                allowedRoles={["superadmin", "admin", "warehouse_manager"]}
                fallback="/not-found"
              />
            }
          >
            <Route
              path="/authorize/requisition"
              element={<AuthorizeRequisition />}
            />
            <Route
              path="/authorization/requisition/detail/:branch_id"
              element={<BranchRequisitionDetails />}
            />
          </Route>
          <Route
            path="/authorization/requisition/dispatch/:requisition_id"
            element={<DispatchShipment />}
          />
          <Route
            path="/authorize/interbranch"
            element={<AuthorizeInterbranch />}
          />


          <Route
            path="/authorize/drug-requisition-detail/:drugId"
            element={<DrugRequisitionReport />}
          />

          {/* ADMINISTRATION */}
          <Route
            path="/administration/*"
            element={
              <RoleBasedRoutes
                path="/administration/*"
                allowedRoles={[
                  "superadmin",
                  "admin",
                  "warehouse_manager",
                  "stores",
                ]}
                component={AdministrationRoutes}
                fallback="/not-found"
              />
            }
          />

          {/* REPORTS */}
          {/* <Route path="/report/*" element={<ReportRoutes />} /> */}

          {/* CASHIER */}
          <Route
            path="/cashier/inventory/batch-tracking"
            element={<BatchTracking />}
          />
          <Route path="/pos/dispenser/" element={<Cashier />} />

          {/* CREDIT SALES */}
          <Route path="/pos/credit" element={<CreditSales />} />

          {/* SALES */}
          <Route path="/sales/*" element={<SalesRoutes />} />

          {/* STOCK */}
          <Route
            path="/stock/outstanding"
            element={<BranchOutstandingStock />}
          />
          <Route
            path="/stock/outstanding/:branch_id"
            element={<BranchOUtstandingStockDetails />}
          />

          {/* CHANGE PASSWORD */}
          <Route path="/user/change-password" element={<ChangePassword />} />

          {/* not found */}
          <Route path="*" element={<NotFound404 />} />
          <Route path="/not-found" element={<NotFound404 />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
        </Routes>
      </div>
    </div>
  );
};

export default Layout;
